import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui';
import Input from '../ui/Input';
import Select from '../ui/Select';
import SearchableSelect from '../ui/SearchableSelect';
import { Users, FileText, CheckCircle, MessageSquare } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../../services/api';

interface MetricData {
  count: number;
}

interface DashboardMetrics {
  converted_trips: MetricData;
  enquiry_requests: MetricData;
  itineraries_created: MetricData;
  agency_confirmations: MetricData;
  pax_travelled: MetricData;
  chart_data: {
    converted_trips: ChartDataPoint[];
    enquiry_requests: PieChartDataPoint[];
    itineraries_created: ChartDataPoint[];
    agency_confirmations: ChartDataPoint[];
    pax_travelled: ChartDataPoint[];
    confirmed_trips_by_agency: PieChartDataPoint[];
    agency_conversions: AgencyConversionDataPoint[];
  };
  date_range: {
    from: string;
    to: string;
  };
  pax_filter?: string;
}

interface ChartDataPoint {
  date: string;
  month: string;
  [key: string]: string | number;
}

interface PieChartDataPoint {
  name: string;
  [key: string]: string | number;
}

interface AgencyConversionDataPoint {
  date: string;
  month: string;
  agency_name: string;
  conversions: number;
}

const DashboardMetrics: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [agencies, setAgencies] = useState<{id: number, name: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [agencyFilter, setAgencyFilter] = useState('');
  const [dateFilters, setDateFilters] = useState({
    dateFrom: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0]
  });
  const [appliedFilters, setAppliedFilters] = useState({
    dateFrom: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchAgencies();
    fetchMetrics();
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [appliedFilters]);

  const handleSearch = () => {
    setAppliedFilters(dateFilters);
  };

  const fetchAgencies = async () => {
    try {
      const response = await api.get('/agencies');
      if (response.data.success) {
        setAgencies(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (appliedFilters.dateFrom) params.append('date_from', appliedFilters.dateFrom);
      if (appliedFilters.dateTo) params.append('date_to', appliedFilters.dateTo);

      const response = await api.get(`/home-metrics?${params}`);
      if (response.data.success) {
        setMetrics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChartTotal = (data: ChartDataPoint[], key: string): number => {
    return data.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
  };

  const getPieChartTotal = (data: PieChartDataPoint[], key: string): number => {
    return data.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
  };

  const getAgencyConversionsTotal = (data: AgencyConversionDataPoint[], agencyFilterValue?: string): number => {
    if (!agencyFilterValue) return data.reduce((sum, item) => sum + (item.conversions || 0), 0);
    const selectedAgency = agencies.find(agency => agency.id.toString() === agencyFilterValue);
    if (!selectedAgency) return 0;
    return data.filter(item => item.agency_name === selectedAgency.name)
               .reduce((sum, item) => sum + (item.conversions || 0), 0);
  };

  const getFilteredAgencyConversions = (data: AgencyConversionDataPoint[], agencyFilterValue?: string): ChartDataPoint[] => {
    let filteredData = data;
    if (agencyFilterValue) {
      const selectedAgency = agencies.find(agency => agency.id.toString() === agencyFilterValue);
      if (selectedAgency) {
        filteredData = data.filter(item => item.agency_name === selectedAgency.name);
      }
    }
    
    // Group by month and sum conversions
    const monthlyData: { [key: string]: { date: string; month: string; conversions: number } } = {};
    filteredData.forEach(item => {
      const key = item.date;
      if (!monthlyData[key]) {
        monthlyData[key] = {
          date: item.date,
          month: item.month,
          conversions: 0
        };
      }
      monthlyData[key].conversions += item.conversions;
    });
    
    return Object.values(monthlyData).sort((a, b) => a.date.localeCompare(b.date));
  };

  const MetricCard: React.FC<{
    title: string;
    data: MetricData;
    icon: React.ReactNode;
    color: string;
  }> = ({ title, data, icon, color }) => (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
        <div className={`p-2 rounded-lg ${color}`}>
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-gray-900">{data.count}</div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none space-y-4 px-1 sm:px-2 lg:px-3 xl:px-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard Metrics</h2>
        
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            type="date"
            value={dateFilters.dateFrom}
            onChange={(e) => setDateFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
            className="w-full sm:w-auto"
          />
          <Input
            type="date"
            value={dateFilters.dateTo}
            onChange={(e) => setDateFilters(prev => ({ ...prev, dateTo: e.target.value }))}
            className="w-full sm:w-auto"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors w-full sm:w-auto"
          >
            Search
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Converted Trips"
            data={metrics.converted_trips}
            icon={<CheckCircle className="w-5 h-5 text-white" />}
            color="bg-green-500"
          />
          <MetricCard
            title="Enquiry Requests"
            data={metrics.enquiry_requests}
            icon={<MessageSquare className="w-5 h-5 text-white" />}
            color="bg-blue-500"
          />
          <MetricCard
            title="Itineraries Created"
            data={metrics.itineraries_created}
            icon={<FileText className="w-5 h-5 text-white" />}
            color="bg-purple-500"
          />
          <MetricCard
            title="PAX Travelled"
            data={metrics.pax_travelled}
            icon={<Users className="w-5 h-5 text-white" />}
            color="bg-red-500"
          />
        </div>
      )}

      {/* Charts */}
      {metrics && metrics.chart_data && (
        <div className="w-full space-y-4">
          {/* First Row - Line Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 lg:gap-4 xl:gap-5 2xl:gap-6">
            <Card className="w-full">
              <CardHeader>
                <CardTitle>
                  Converted Trips (by Travel Date) - Total: {metrics.chart_data.converted_trips ? getChartTotal(metrics.chart_data.converted_trips, 'converted_trips').toLocaleString() : 0}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {metrics.chart_data.converted_trips && metrics.chart_data.converted_trips.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={metrics.chart_data.converted_trips}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="converted_trips" stroke="#10b981" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="w-full">
              <CardHeader>
                <CardTitle>
                  Itineraries Created (by Creation Date) - Total: {metrics.chart_data.itineraries_created ? getChartTotal(metrics.chart_data.itineraries_created, 'itineraries_created').toLocaleString() : 0}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {metrics.chart_data.itineraries_created && metrics.chart_data.itineraries_created.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={metrics.chart_data.itineraries_created}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="itineraries_created" stroke="#8b5cf6" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Second Row - Line and Pie Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 lg:gap-4 xl:gap-5 2xl:gap-6">
            <Card className="w-full">
              <CardHeader>
                <CardTitle>
                  PAX Travelled (by Travel Date) - Total: {metrics.chart_data.pax_travelled ? getChartTotal(metrics.chart_data.pax_travelled, 'pax_travelled').toLocaleString() : 0}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {metrics.chart_data.pax_travelled && metrics.chart_data.pax_travelled.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={metrics.chart_data.pax_travelled}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="pax_travelled" stroke="#ef4444" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="w-full">
              <CardHeader>
                <CardTitle>
                  Enquiry Requests by Agency - Total: {metrics.chart_data.enquiry_requests ? getPieChartTotal(metrics.chart_data.enquiry_requests, 'enquiry_requests').toLocaleString() : 0}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {metrics.chart_data.enquiry_requests && metrics.chart_data.enquiry_requests.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={metrics.chart_data.enquiry_requests}
                        dataKey="enquiry_requests"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#3b82f6"
                        label
                      >
                        {metrics.chart_data.enquiry_requests.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 50%)`} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Third Row - Pie Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 lg:gap-4 xl:gap-5 2xl:gap-6">
            <Card className="w-full">
              <CardHeader>
                <CardTitle>
                  Confirmed Trips by Agency - Total: {metrics.chart_data.confirmed_trips_by_agency ? getPieChartTotal(metrics.chart_data.confirmed_trips_by_agency, 'confirmed_trips').toLocaleString() : 0}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {metrics.chart_data.confirmed_trips_by_agency && metrics.chart_data.confirmed_trips_by_agency.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={metrics.chart_data.confirmed_trips_by_agency}
                        dataKey="confirmed_trips"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#10b981"
                        label
                      >
                        {metrics.chart_data.confirmed_trips_by_agency.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(${index * 60}, 70%, 50%)`} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Fourth Row - Agency Conversions Chart */}
          <div className="w-full">
            <Card className="w-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>
                  Agency Conversions Per Month - Total: {metrics.chart_data.agency_conversions ? getAgencyConversionsTotal(metrics.chart_data.agency_conversions, agencyFilter).toLocaleString() : 0}
                </CardTitle>
                <SearchableSelect
                  options={[
                    { value: '', label: 'All Agencies' },
                    ...agencies.map(agency => ({ value: agency.id.toString(), label: agency.name }))
                  ]}
                  value={agencyFilter}
                  onChange={(value) => setAgencyFilter(value as string)}
                  placeholder="Select Agency"
                  className="w-64"
                />
              </CardHeader>
              <CardContent>
                {metrics.chart_data.agency_conversions && metrics.chart_data.agency_conversions.length > 0 ? (
                  <ResponsiveContainer width="100%" height={500}>
                    <LineChart data={getFilteredAgencyConversions(metrics.chart_data.agency_conversions, agencyFilter)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="conversions" stroke="#f59e0b" strokeWidth={2} name="Conversions" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-64 text-gray-500">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Date Range Info */}
      {metrics && (
        <div className="text-sm text-gray-500 text-center py-4">
          Showing data from {new Date(metrics.date_range.from).toLocaleDateString()} to {new Date(metrics.date_range.to).toLocaleDateString()}
          {agencyFilter && ` • Agency: ${agencies.find(a => a.id.toString() === agencyFilter)?.name || agencyFilter}`}
        </div>
      )}
    </div>
  );
};

export default DashboardMetrics;